import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from 'state/axios';
import { format } from "date-fns";
import {
  useTheme,
  useMediaQuery,
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { 
  AddCircleOutline, 
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from "hooks/useAuthCheck";
import { LAYOUT_HEIGHT } from "state/constants";
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import Header from "components/Header";
import StyledButton from 'components/StyledButton';
import DeleteConfirm from "components/DeleteConfirm";
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const Contributions = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const greenDeep = theme.palette.green.deep;

  const canCreate = hasPermission(user?.permissions, "Contributions", "create", false);
  const canEdit = hasPermission(user?.permissions, "Contributions", "update", false);
  const canDelete = hasPermission(user?.permissions, "Contributions", "delete", false);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [contributions, setContributions] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: "include",
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  
  useEffect(() => {
    const fetchContributions = async () => {
      try {
        setError(null);
        setLoading(true);

        const res = await api.get('/contributions');
        setContributions(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchContributions();
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      width: 60,
      renderCell: (params) =>
        params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "contributionObject",
      headerName: "ContributionObject",
      width: 125,
    },
    {
      field: "description",
      headerName: "Description",
      width: 300,      
    },
    {
      field: "contributionRate",
      headerName: "contributionRate",
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const list = params.row.contributionRate || [];
        if (!list.length) return "";

        return (
          <Box
            sx={{
              height: '100%',
              display: "flex",
              flexDirection: "column",
              overflowY: 'auto',
              overflowX: 'hidden',
              gap: 0.5,
              pr: 1,
            }}
          >
            {list.map((item, index) => (
              <Box
                key={index}
                display="grid"
                gridTemplateColumns="60px 1fr auto"
                alignItems="center"
                columnGap={1}
                
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    whiteSpace: "normal",     // cho phép xuống dòng
                    wordBreak: "break-word",  // tự ngắt từ khi cần
                    overflowWrap: "anywhere"  // fallback an toàn
                  }}
                >
                  {item.contributionCode}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: "left", width: 80 }}>
                  {item.employeeRate ? `Employee: ${item.employeeRate}%` : ""}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: "left", width: 80 }}>
                  {item.employerRate ? `Employer: ${item.employerRate}%` : ""}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },    
    {
      field: "effectiveFrom",
      headerName: "EffectiveFrom",
      width: 100,
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
    },
    {
      field: "effectiveTo",
      headerName: "EffectiveTo",
      width: 100,
      valueFormatter: (value) => {
        if (value == null) {
          return '';
        }
        return `${format(value, "dd/MM/yyyy")}`;
      },
    },
    {
      field: "sortOrder",
      headerName: "SortOrder",
      width: 90,
      align: "right",
    },
    {
      field: "isActive",
      headerName: "IsActive",
      width: 90,
    },    
  ], []);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      filterable: false,
      sortable: false,
      width: 90,
      renderCell: (params) => {
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            sx={{ width: "100%", height: "100%" }}
          >
            <Tooltip title="Edit">
              <IconButton
                aria-label="Edit"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => {
                  if (!canEdit) return;
                  navigate(`/contributions/${params.row._id}`)
                }}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          
            <Tooltip title="Delete">
              <IconButton
                aria-label="Delete"
                sx={{
                  color: primaryDark,
                  transition: 'color 0.3s ease, background-color 0.3s ease',
                  '&:hover': {
                    color: greenDeep,
                  },
                }}
                onClick={() => {
                  if (!canDelete) return;
                  handleDeleteClick(params.row._id);                  
                }}
              >
                <DeleteForever />
              </IconButton>                
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType("single");
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, contributions);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }

    setDeleteType("bulk");
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === "single") {
        if (!idToDelete) return;
        await api.delete(
          `/contributions/delete/${idToDelete}`
        );
        setContributions(contributions.filter((item) => item._id !== idToDelete));
        toast.success("Contribution deleted successfully.");
      } else if (deleteType === "bulk") {
        const selectedIds = getSelectedIds(rowSelectionModel, contributions);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const url = '/contributions/soft-delete';
        const payload = { ids: selectedIds };        
        const res = await api.patch(url, payload);        
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setContributions(contributions.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete contributions!");
    }
  }, [
    canDelete, 
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    contributions,
  ]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Contributions"
          subtitle={`${loading ? "Loading..." : "List of Contributions"}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>            
            {!isMobile && (<Tooltip title="Bulk soft delete" arrow>
              <span>
                <StyledButton 
                  disabled={!isAnyRowSelected(rowSelectionModel, contributions)}
                  onClick={() => {
                    if (!canDelete) return;
                    handleBulkDelete();
                  }}
                >
                  <Delete />
                </StyledButton>
              </span>              
            </Tooltip>)}                       

            <Tooltip title="Create" arrow>
              <span>
                <StyledButton 
                  onClick={() => {
                    if (!canCreate) return;
                    navigate("/contributions/new");
                  }}
                >
                  <AddCircleOutline />
                </StyledButton>
              </span>              
            </Tooltip>
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <Typography color="error">Error: {error?.message || ''}</Typography>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
        >
          <Box
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12" }}
          >
            {isMobile ? (
              <CardList
                rows={contributions}
                primaryField="contributionObject"
                fields={[
                  { label: "Description", key: "description" },
                  { label: "Effective From", key: "effectiveFrom" },
                  { label: "Effective To", key: "effectiveTo" },
                  { label: "Contribution Rate", key: "contributionRate" },
                  { label: "Sort Order", key: "sortOrder" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/contributions/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
            <DataGridComponent
              rows={contributions || []}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                sx: {
                  "& .MuiDataGrid-row.isActive-false": {
                    backgroundColor: `${theme.palette.primary.light} !important`,  
                  },
                },
              }}
            />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Contributions;